import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { incidentService, propertyService } from "@iso/services";

// Permission IDs from the `permissions` table — see PermissionController
// (1 = owner, 2 = admin). Anything else (modify / view) is not a manager.
const MANAGER_PERMISSION_IDS = [1, 2];

/**
 * Returns `{ canManageProperty, canManageCorporate }` — whether the
 * current user is allowed to assign-user / set-expiry / bulk-manage
 * files in each scope. The two values mirror the backend's
 * AttachmentPermissionService rules:
 *
 *   canManageProperty:
 *     - active permission slug is "owner" or "admin", OR
 *     - is_safety_admin on the active property (incident settings).
 *
 *   canManageCorporate:
 *     - owner/admin permission on any property in the active property's
 *       corporate group.
 *     (Safety roles are not consulted here — surfacing them would require
 *      one incidentService.getSettings() round-trip per property in the
 *      group, which isn't worth it for a UI-affordance check. The backend
 *      remains authoritative; users with only a safety role on a corporate
 *      property will see buttons hidden but can still call the API.)
 *
 * Callers pick the relevant flag based on the file manager's active scope.
 */
const useCanManageFiles = () => {
  const [canManageProperty, setCanManageProperty] = useState(false);
  const [canManageCorporate, setCanManageCorporate] = useState(false);

  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const activePermission = useSelector(
    (state) => state.permission?.activePermission
  );

  // Property-scope check (existing behaviour).
  useEffect(() => {
    if (!activePropertyId) {
      setCanManageProperty(false);
      return;
    }
    if (["owner", "admin"].includes(activePermission)) {
      setCanManageProperty(true);
      return;
    }
    let cancelled = false;
    incidentService
      .getSettings(activePropertyId)
      .then((response) => {
        if (cancelled) return;
        setCanManageProperty(Boolean(response?.is_safety_admin));
      })
      .catch(() => {
        if (!cancelled) setCanManageProperty(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activePropertyId, activePermission]);

  // Corporate-scope check — owner/admin on any property in the same group.
  // Uses the property list (which exposes per-property permission_id) rather
  // than activePermission alone, because the user may be admin on property B
  // even when property A is currently active.
  useEffect(() => {
    if (!activePropertyId) {
      setCanManageCorporate(false);
      return;
    }
    let cancelled = false;
    propertyService
      .getAllProperties()
      .then((res) => {
        if (cancelled) return;
        const properties = res?.properties || [];
        const active = properties.find((p) => p.id === activePropertyId);
        const groupId = active?.corporate_group_id;
        if (!groupId) {
          setCanManageCorporate(false);
          return;
        }
        const isManager = properties.some(
          (p) =>
            p.corporate_group_id === groupId &&
            MANAGER_PERMISSION_IDS.includes(p.permission_id)
        );
        setCanManageCorporate(isManager);
      })
      .catch(() => {
        if (!cancelled) setCanManageCorporate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activePropertyId]);

  return { canManageProperty, canManageCorporate };
};

export default useCanManageFiles;
